import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { ApiParamsType } from '@/features/shared/types/ApiParamsType';
import { OneRosterApiTyped } from '@/lib/http.server';
import { ELEVATED_ROLES } from '@/lib/constants/auth.constants';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';
import { assertUserHasRole } from '@/middlewares/userAuthMiddleware';
import { OrgTypeEnum, OrgTypeEnumValues } from '@/types/common';

import { Organization } from '../validators/schemas';

type OrganizationsResponse = {
  orgs?: Organization[];
  totalCount: number;
  pageCount: number;
  pageNumber: number;
  offset: number;
  limit: number;
};

type GetOrganizationsResponse = {
  data: OrganizationsResponse;
  success: boolean;
  error: string | null;
};

const getOrganizationsDefaultParamsValues = {
  fields:
    'sourcedId,name,status,type,identifier,metadata,parent,children,dateLastModified',
  filter: `status='active'`,
  sort: 'name',
  orderBy: 'asc',
  limit: 100,
  offset: 0,
  search: '',
  sourcedIds: [] as string[],
} as const;

type GetOrganizationsParams = ApiParamsType & {
  sourcedIds?: string[];
  orgTypes?: OrgTypeEnum[];
};

export const getOrganizationsDefaultParams =
  getOrganizationsDefaultParamsValues satisfies GetOrganizationsParams;

const organizationsPaginatedParamsSchema = z.object({
  fields: z
    .string()
    .optional()
    .default(getOrganizationsDefaultParamsValues.fields),
  limit: z
    .number()
    .optional()
    .default(getOrganizationsDefaultParamsValues.limit),
  filter: z
    .string()
    .optional()
    .default(getOrganizationsDefaultParamsValues.filter),
  offset: z
    .number()
    .optional()
    .default(getOrganizationsDefaultParamsValues.offset),
  search: z
    .string()
    .optional()
    .default(getOrganizationsDefaultParamsValues.search),
  sourcedIds: z
    .array(z.string())
    .optional()
    .default(getOrganizationsDefaultParamsValues.sourcedIds),
  orgTypes: z.array(z.enum(OrgTypeEnumValues)).optional(),
});

const fetchOnerosterOrganizations = async (
  onerosterApi: OneRosterApiTyped,
  params: GetOrganizationsParams
) => {
  let filter = getOrganizationsDefaultParams.filter;
  const { limit, offset, search, orgTypes } = params;

  if (orgTypes && orgTypes.length > 0) {
    filter += ` AND (type='${orgTypes.join("' OR type='")}')`;
  }

  if (search) {
    filter += ` AND name~'${search}'`;
  }

  if (params.sourcedIds && params.sourcedIds.length > 0) {
    filter += ` AND (sourcedId='${params.sourcedIds.join("' OR sourcedId='")}')`;
  }

  const response = await onerosterApi.GET(
    '/ims/oneroster/rostering/v1p2/orgs/',
    {
      params: {
        query: {
          ...getOrganizationsDefaultParams,
          limit,
          offset,
          filter,
          search,
        },
      },
    }
  );

  return response.data;
};

export const getOrganizations = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(
    organizationsPaginatedParamsSchema.extend({
      sourcedIds: z.array(z.string()).optional(),
    })
  )
  .handler(async ({ context, data }): Promise<GetOrganizationsResponse> => {
    const { oneRosterApiTyped, cognitoUser } = context;
    await assertUserHasRole(cognitoUser, oneRosterApiTyped, ELEVATED_ROLES);

    const response = await fetchOnerosterOrganizations(oneRosterApiTyped, data);

    return {
      success: true,
      data: response,
      error: null,
    } as GetOrganizationsResponse;
  });
